import styled, { css, useTheme } from "styled-components";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import { toFixed } from "@shared/utils/numbers";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { AnimatePresence, motion } from "motion/react";
import { typography } from "@shared/common/Themes/typography";
import { Close } from "common/Icons/Icons";
import { Underline, Value } from "features/tradingTerminal/common/styles";
import OrderNumber, {
  Wrapper as OrderNumberWrapper,
} from "common/Numbering/OrderNumber";
import { X } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import useOrder from "@shared/features/tradingTerminal/common/hooks/useOrder";
import { OrderProps } from "@shared/features/tradingTerminal/common/types";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { useContext } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { OrderTypes } from "@shared/types";

const Order = ({
  order,
  index,
  updateOrder,
  orderType,
  amount,
  removeOrder,
  orderSectionErrors,
  baselineEntryPrice,
  readonly,
  columnSpans,
  ignoreRatios,
}: OrderProps) => {
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    percentPrecision,
    setFocusedInputType,
    focusedInputErrorType,
    orderKeysWithErrors,
    distanceDisplay,
    percentDisplay,
    errorMessage,
    getDistance,
    onChange,
    onBlur,
    precisionConfig,
  } = useOrder({
    order,
    index,
    updateOrder,
    orderType,
    amount,
    orderSectionErrors,
    baselineEntryPrice,
  });
  const { isSignal } = useContext(TradingTerminalContext)!;
  const isSignalEntry = isSignal && orderType === OrderTypes.ENTRY;

  const inputBgColor = isBottomNavigator
    ? theme.colors.layoutBgColor
    : theme.colors.cardBgColor;
  // New look: inline values are compact fields that fill their column, so
  // the rows line up like a small table.
  const autoSizeInputs = !theme.isNewLook;
  const orderNumber = <OrderNumber displayNumber={index + 1} />;

  const readonlyOrder = (
    <>
      <Col span={columnSpans?.[0]}>
        <Flex $justifyContent="flex-start">
          {orderNumber}
          <Value $danger={orderKeysWithErrors.has("price")}>
            {order.price} ({order.distance ?? getDistance()}%)
          </Value>
        </Flex>
      </Col>
      <Col span={columnSpans?.[1]}>
        <Flex $justifyContent="flex-start">
          {order.amount && (
            <Value
              $danger={orderKeysWithErrors.has("amount")}
              $warning={order.amount === "0"}
              style={{ paddingRight: 4 }}
            >
              {order.amount}
            </Value>
          )}
          {!ignoreRatios && (
            <Value
              $danger={orderKeysWithErrors.has("percent")}
              $warning={order.percent === "0"}
            >
              {order.amount ? `(${percentDisplay})` : percentDisplay}
            </Value>
          )}
        </Flex>
      </Col>
    </>
  );

  const getEditableOrderAmount = () => {
    return (
      <Col span={columnSpans?.[2]}>
        {!isSignalEntry && (
          <Flex>
            {"amount" in order ? (
              <Underline
                $danger={orderKeysWithErrors.has("amount")}
                $warning={order.amount === "0"}
              >
                <CustomInputNumber
                  bordered={false}
                  value={toFixed(order.amount, precisionConfig?.amount)}
                  onChange={(amount: string) => onChange("amount", amount)}
                  precision={precisionConfig?.amount || 0}
                  stringMode={true}
                  placeholder={"0"}
                  onFocus={() => setFocusedInputType("amount")}
                  onBlur={onBlur}
                  autoSize={autoSizeInputs}
                  useDebounce={true}
                  bgColor={inputBgColor}
                />
              </Underline>
            ) : (
              <DistanceText>{distanceDisplay}</DistanceText>
            )}
          </Flex>
        )}
      </Col>
    );
  };

  const editedOrder = (
    <>
      <Col span={columnSpans?.[0]}>
        <Flex $justifyContent={"start"}>
          {orderNumber}
          <Underline $danger={orderKeysWithErrors.has("price")}>
            <CustomInputNumber
              bordered={false}
              value={toFixed(Number(order.price), precisionConfig?.price)}
              onChange={(price: string) => onChange("price", price)}
              precision={precisionConfig?.price || 0}
              stringMode={true}
              onFocus={() => setFocusedInputType("price")}
              onBlur={onBlur}
              autoSize={autoSizeInputs}
              useDebounce={true}
              bgColor={inputBgColor}
            />
          </Underline>
        </Flex>
      </Col>
      <Col span={columnSpans?.[1]}>
        {!ignoreRatios && (
          <Flex>
            {orderType === OrderTypes.STOP ? (
              <Value $danger={orderKeysWithErrors.has("percent")}>
                {order.percent}%
              </Value>
            ) : (
              <Underline
                $danger={orderKeysWithErrors.has("percent")}
                $warning={order.percent === "0"}
              >
                <CustomInputNumber
                  bordered={false}
                  value={toFixed(
                    Number(order.percent),
                    precisionConfig?.percent
                  )}
                  onChange={(percent: string) => onChange("percent", percent)}
                  precision={percentPrecision}
                  stringMode={true}
                  placeholder={"0"}
                  onFocus={() => setFocusedInputType("percent")}
                  onBlur={onBlur}
                  autoSize={autoSizeInputs}
                  useDebounce={true}
                  bgColor={inputBgColor}
                />
                <Text>%</Text>
              </Underline>
            )}
          </Flex>
        )}
      </Col>
      {getEditableOrderAmount()}
      {orderType !== OrderTypes.STOP && (
        <IconWrapper
          onClick={(e: React.MouseEvent) => {
            removeOrder?.(orderType, index);
            e.stopPropagation();
          }}
        >
          <LookIcon
            classic={<CloseIcon />}
            newLook={<X size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />}
          />
        </IconWrapper>
      )}
    </>
  );

  return (
    <>
      <Wrapper>{readonly ? readonlyOrder : editedOrder}</Wrapper>
      <AnimatePresence>
        {errorMessage && (
          <Text
            type={focusedInputErrorType !== undefined ? "danger" : "secondary"}
            as={motion.div}
            $size={typography.fontSizes.S}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            {errorMessage}
          </Text>
        )}
      </AnimatePresence>
    </>
  );
};

export default Order;

/** Width reserved at the end of each order row for the remove button. */
export const NEW_LOOK_REMOVE_COLUMN = 28;

const Wrapper = styled(Row)`
  height: 24px;
  margin-bottom: 8px;
  position: relative;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: 32px;
      align-items: center;
      padding-right: ${NEW_LOOK_REMOVE_COLUMN}px;
      margin-bottom: 0;
      ${OrderNumberWrapper} {
        width: 18px;
        height: 18px;
        margin-right: 8px;
        border: none;
        background: ${p.theme.colors.cardLightBgColor};
        font-size: 11px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        font-variant-numeric: tabular-nums;
      }
      .ant-col > div {
        min-width: 0;
      }
    `}
`;
const DistanceText = styled(Text)`
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-variant-numeric: tabular-nums;
    color: ${p.theme.colors.secondaryText};
  `}
`;
const IconWrapper = styled.div`
  position: absolute;
  margin: 2px;
  cursor: pointer;
  padding: 4px;
  right: 0;
  top: -3px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    top: 50%;
    margin: 0;
    transform: translateY(-50%);
    display: flex;
    border-radius: 6px;
    color: ${p.theme.colors.secondaryText};
    transition: color 150ms, background-color 150ms;
    &:hover {
      color: ${p.theme.colors.red};
      background: ${p.theme.colors.cardLightBgColor};
    }
  `}
`;
const CloseIcon = styled(Close)`
  font-size: 10px;
  color: ${(p) => p.theme.colors.basic};
`;
